<script setup lang="ts">
import { computed } from "vue";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
} from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { displayFileName } from "../lib/fileNames";
import type { EntityLink } from "../lib/entityFrontmatter";
import {
  inverseRelation,
  listEntityRelationships,
  newEntityLinkId,
  relationLabel,
  type EntityRelationshipRow,
} from "../lib/entityLinks";
import { openContextMenu } from "../lib/platform/contextMenu";
import { useTea } from "../lib/tea";
import {
  getProjectDatabaseEntryFields,
  updateProjectDatabaseEntryFields,
} from "../lib/tauri/database";
import { useEntityMapRefs } from "../composables/useEntityMapRefs";
import { requestMapLandmarkFocus } from "../lib/mapLandmarkFocus";
import type { MapLandmarkRef } from "../lib/mapLandmarkLinks";
import { useActiveEntityFieldsStore } from "../stores/useActiveEntityFieldsStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import type { ZwDatabaseRelationPair } from "../lib/zwConfig";
import EntityMapRefsPanel from "./EntityMapRefsPanel.vue";
import EntityRelationshipDialog from "./EntityRelationshipDialog.vue";
import {
  initialEntityLinkDialogModel,
  updateEntityLinkDialogFn,
  type EntityLinkDialogCmd,
} from "./EntityLinksPanel.update";
import ZwButton from "./ui/ZwButton.vue";

const session = useEditorSessionStore();
const outlineEntities = useOutlineEntitiesStore();
const entityFields = useActiveEntityFieldsStore();
const workspace = useProjectWorkspaceStore();
const { refs: mapRefs, hasRefs: hasMapRefs } = useEntityMapRefs();

const activeFile = computed(() => session.activeDatabase);

const customPairs = computed((): Maybe<readonly ZwDatabaseRelationPair[]> => {
  if (isNothing(session.projectConfig)) return nothing;
  return just(session.projectConfig.value.databaseRelationPairs);
});

const relationships = computed(() =>
  matchMaybe(activeFile.value, {
    nothing: () => [],
    just: (file) =>
      listEntityRelationships(
        file,
        entityFields.fields.links,
        outlineEntities.databaseEntryFields,
        customPairs.value,
      ),
  }),
);

const relationshipsEmpty = computed(
  () => relationships.value.length === 0 && hasMapRefs.value === false,
);

function openMapRef(row: MapLandmarkRef): void {
  requestMapLandmarkFocus(row.mapFile, row.landmarkId);
  void workspace.onSelectProjectDatabase(row.mapFile);
  if (session.mobileShell) {
    session.contextPanelOpen = false;
  }
}

function relationshipPhraseLabel(row: EntityRelationshipRow): string {
  const relationId =
    row.direction === "in"
      ? inverseRelation(row.link.relation, customPairs.value)
      : row.link.relation;
  return relationLabel(relationId, customPairs.value);
}

async function runLinkDialogCmd(cmd: EntityLinkDialogCmd): Promise<void> {
  switch (cmd.kind) {
    case "save":
      try {
        const root = fromMaybe(session.rootPath);
        if (root === null) {
          void dispatchLink({ type: "SaveFailed" });
          return;
        }
        const activeName = isJust(activeFile.value)
          ? activeFile.value.value
          : "";
        const currentLinks =
          cmd.sourceFile === activeName
            ? entityFields.fields.links
            : (await getProjectDatabaseEntryFields(root, cmd.sourceFile)).links;
        const nextLink: EntityLink = {
          id: matchMaybe(cmd.editingLinkId, {
            nothing: () => newEntityLinkId(),
            just: (id) => id,
          }),
          file: cmd.target,
          relation: cmd.relation,
          note: cmd.note.length > 0 ? just(cmd.note) : nothing,
        };
        const editingId = cmd.editingLinkId;
        const nextLinks = isJust(editingId)
          ? currentLinks.map((link) =>
              link.id === editingId.value ? nextLink : link,
            )
          : [...currentLinks, nextLink];
        await persistLinksOnSource(cmd.sourceFile, nextLinks);
        void dispatchLink({ type: "SaveSucceeded" });
      } catch (error) {
        console.error("Failed to save relationship", error);
        window.alert(
          error instanceof Error
            ? error.message
            : "Could not save relationship.",
        );
        void dispatchLink({ type: "SaveFailed" });
      }
      return;
    case "remove":
      try {
        const root = fromMaybe(session.rootPath);
        if (root === null) {
          void dispatchLink({ type: "RemoveFailed" });
          return;
        }
        const activeName = isJust(activeFile.value)
          ? activeFile.value.value
          : "";
        const currentLinks =
          cmd.sourceFile === activeName
            ? entityFields.fields.links
            : (await getProjectDatabaseEntryFields(root, cmd.sourceFile)).links;
        await persistLinksOnSource(
          cmd.sourceFile,
          currentLinks.filter((link) => link.id !== cmd.linkId),
        );
        void dispatchLink({
          type: "RemoveSucceeded",
          removedLinkId: cmd.linkId,
        });
      } catch (error) {
        console.error("Failed to remove relationship", error);
        window.alert(
          error instanceof Error
            ? error.message
            : "Could not remove relationship.",
        );
        void dispatchLink({ type: "RemoveFailed" });
      }
      return;
    default:
      assertNever(cmd);
  }
}

const { model: linkModel, dispatch: dispatchLink } = useTea(
  initialEntityLinkDialogModel,
  updateEntityLinkDialogFn,
  { runCmd: just(runLinkDialogCmd) },
);

const linkDialogOpen = computed(() => linkModel.value.open);
const editingSourceFile = computed(() => linkModel.value.sourceFile);
const editingLinkId = computed(() => linkModel.value.editingLinkId);
const linkBusy = computed(() => linkModel.value.busy);
const linkTarget = computed({
  get: (): string => linkModel.value.target,
  set: (target: string): void => {
    void dispatchLink({ type: "TargetChanged", target });
  },
});
const linkRelation = computed({
  get: (): string => linkModel.value.relation,
  set: (relation: string): void => {
    void dispatchLink({ type: "RelationChanged", relation });
  },
});
const linkNote = computed({
  get: (): string => linkModel.value.note,
  set: (note: string): void => {
    void dispatchLink({ type: "NoteChanged", note });
  },
});

const linkableEntries = computed(() =>
  outlineEntities.databaseEntryFields
    .filter(
      (entry) =>
        entry.isMap === false && entry.name !== editingSourceFile.value,
    )
    .map((entry) => ({
      value: entry.name,
      label: displayFileName(entry.name),
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, "en", { sensitivity: "base" }),
    ),
);

const canAddRelationship = computed(() =>
  matchMaybe(activeFile.value, {
    nothing: () => false,
    just: (file) =>
      outlineEntities.databaseEntryFields.some(
        (entry) => entry.isMap === false && entry.name !== file,
      ),
  }),
);

function openCreateRelationship(): void {
  if (isNothing(activeFile.value)) return;
  const file = activeFile.value.value;
  const defaultTarget =
    outlineEntities.databaseEntryFields.find(
      (entry) => entry.isMap === false && entry.name !== file,
    )?.name ?? "";
  void dispatchLink({
    type: "OpenCreate",
    sourceFile: file,
    defaultTarget,
  });
}

function openEditRelationship(row: EntityRelationshipRow): void {
  void dispatchLink({
    type: "OpenEdit",
    sourceFile: row.sourceFile,
    linkId: row.link.id,
    target: row.link.file,
    relation: row.link.relation,
    note: matchMaybe(row.link.note, {
      just: (note) => note,
      nothing: () => "",
    }),
  });
}

function closeLinkDialog(): void {
  void dispatchLink({ type: "Close" });
}

/** Write `links` on `sourceFile` without navigating. Uses the active draft
 * when that file is open, otherwise a disk read/write like the graph. */
async function persistLinksOnSource(
  sourceFile: string,
  nextLinks: EntityLink[],
): Promise<void> {
  const root = fromMaybe(session.rootPath);
  if (root === null) return;

  const activeName = isJust(activeFile.value) ? activeFile.value.value : "";
  const base =
    sourceFile === activeName
      ? { ...entityFields.fields, links: nextLinks }
      : {
          ...(await getProjectDatabaseEntryFields(root, sourceFile)),
          links: nextLinks,
        };

  if (sourceFile === activeName) {
    // Keep the open draft aligned before the awaited write so a debounced
    // flush cannot overwrite the new links with a stale snapshot.
    entityFields.setField("links", nextLinks, { immediate: true });
  }

  await updateProjectDatabaseEntryFields(root, sourceFile, base);
  await outlineEntities.refreshDatabase();
}

function commitRelationship(): void {
  void dispatchLink({ type: "Commit" });
}

async function onRelationshipContextMenu(
  event: MouseEvent,
  row: EntityRelationshipRow,
): Promise<void> {
  await openContextMenu(event, [
    {
      id: `remove-relationship-${row.key}`,
      label: "Remove relationship",
      action: () =>
        void dispatchLink({
          type: "Remove",
          sourceFile: row.sourceFile,
          linkId: row.link.id,
        }),
    },
  ]);
}

function removeEditingRelationship(): void {
  void dispatchLink({ type: "RemoveEditing" });
}
</script>

<template>
  <section class="entity-links-section">
    <div class="entity-links-header">
      <h3>Relationships</h3>
      <ZwButton
        size="sm"
        :disabled="canAddRelationship === false"
        @click="openCreateRelationship"
      >
        + Add
      </ZwButton>
    </div>
    <p v-if="relationshipsEmpty" class="entity-links-empty">
      No relationships yet.
    </p>
    <ul v-if="relationships.length > 0" class="entity-links-list">
      <li
        v-for="row in relationships"
        :key="row.key"
        class="entity-link-row"
        @contextmenu="onRelationshipContextMenu($event, row)"
      >
        <button
          type="button"
          class="entity-link-main"
          @click="openEditRelationship(row)"
        >
          <span class="entity-link-phrase">
            <span class="entity-link-relation">{{
              relationshipPhraseLabel(row)
            }}</span>
            <span class="entity-link-target">{{
              displayFileName(row.otherFile)
            }}</span>
          </span>
          <span v-if="row.link.note.tag === 'just'" class="entity-link-note">{{
            row.link.note.value
          }}</span>
        </button>
      </li>
    </ul>
    <EntityMapRefsPanel :items="mapRefs" @open-map="openMapRef" />
  </section>

  <EntityRelationshipDialog
    v-model:target-file="linkTarget"
    v-model:relation="linkRelation"
    v-model:note="linkNote"
    :open="linkDialogOpen"
    :source-file="editingSourceFile"
    :editing="isJust(editingLinkId)"
    :busy="linkBusy"
    :linkable-entries="linkableEntries"
    @close="closeLinkDialog"
    @save="commitRelationship"
    @remove="removeEditingRelationship"
  />
</template>

<style scoped>
.entity-links-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-bottom: 0.35rem;
}

.entity-links-header h3 {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.entity-links-empty {
  margin: 0 0.35rem;
  font-size: 0.85rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.entity-links-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.entity-link-row {
  border-radius: 0.25rem;
}

.entity-link-row:hover {
  background: var(--hover-fill);
}

.entity-link-main {
  appearance: none;
  border: 0;
  background: transparent;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.08rem;
  padding: 0.35rem 0.45rem;
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.entity-link-phrase {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem;
  min-width: 0;
}

.entity-link-target {
  font-size: 0.88rem;
  color: var(--text);
}

.entity-link-relation {
  font-size: 0.78rem;
  color: var(--text-muted);
}

.entity-link-note {
  font-size: 0.78rem;
  line-height: 1.35;
  color: color-mix(in oklch, var(--text-muted) 90%, transparent);
}
</style>
